import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { GetRegisterFields } from "../../../services/RegisterService";
import Modal from "../../Modal/Modal";

const RegistrationFormContext = createContext();

export const RegistrationFormProvider = ({ children }) => {
	const [registerData, setRegisterData] = useState(null);
	const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
	const [showTimeoutModal, setShowTimeoutModal] = useState(false);
	const timerRef = useRef(null);

	/**
	 * Load registerData from local storage if it exists
	 */
	useEffect(() => {
		const data = sessionStorage.getItem("registerData");
		if (data) {
			setRegisterData(JSON.parse(data));
		}
	}, []);

	/**
	 * Save registerData to local storage when it changes
	 */
	useEffect(() => {
		if (registerData) {
			sessionStorage.setItem("registerData", JSON.stringify(registerData));
		} else {
			sessionStorage.removeItem("registerData");
		}
	}, [registerData]);

	const stopTemp = async () => {
		try {
			const newPayload = {
				phone_number: registerData?.mobile_number,
				num_kids: registerData?.selectedKids?.length || 0,
				is_premium: registerData?.is_premium ? 1 : 0
			}

			const url = `${process.env.REACT_APP_API_URL}stopTemp`;

			const newResponse = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(newPayload)
			});

			if (!newResponse.ok) {
				throw new Error(`HTTP error! status: ${newResponse.status}`);
			}

			const data = await newResponse.json();
			console.log('stopTemp response:', data);
		} catch (error) {
			console.error('Error in stopTemp:', error);
		}
	}

	// Timer effect — runs once on mount, resumes if timerEndTime already in sessionStorage
	useEffect(() => {
		const endTime = sessionStorage.getItem("timerEndTime");
		if (!endTime) return;

		const remainingTime = Math.max(0, Math.floor((parseInt(endTime) - Date.now()) / 1000));
		setTimeLeft(remainingTime);

		if (remainingTime <= 0) {
			stopTemp();
			setShowTimeoutModal(true);
			return;
		}

		timerRef.current = setInterval(() => {
			const currentRemainingTime = Math.max(0, Math.floor((parseInt(endTime) - Date.now()) / 1000));
			setTimeLeft(currentRemainingTime);

			if (currentRemainingTime <= 0) {
				clearInterval(timerRef.current);
				stopTemp();
				setShowTimeoutModal(true);
			}
		}, 1000);

		return () => {
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Format time as MM:SS
	const formatTime = (seconds) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
	};

	const handleTimeoutContinue = () => {
		setShowTimeoutModal(false);
		sessionStorage.removeItem("timerEndTime");
		setRegisterData(null);
		setTimeLeft(900);
	};

	const startTimer = () => {
		// Reset the timer
		if (timerRef.current) {
			clearInterval(timerRef.current);
		}

		const newEndTime = Date.now() + 900000; // 15 minutes from now
		sessionStorage.setItem("timerEndTime", newEndTime.toString());
		setTimeLeft(900);

		// Start the timer
		timerRef.current = setInterval(() => {
			const currentRemainingTime = Math.max(0, Math.floor((parseInt(newEndTime) - Date.now()) / 1000));
			setTimeLeft(currentRemainingTime);
			
			if (currentRemainingTime <= 0) {
				clearInterval(timerRef.current);
				(async () => {
					try {
						await stopTemp();
					} catch (error) {
						console.error('Помилка при виклику stopTemp:', error);
					} finally {
						setShowTimeoutModal(true);
					}
				})();
			}
		}, 1000);
	};

	const {
		isLoading: isLoadingRegisterFields,
		data: registerFields,
		isError: isErrorRegisterFields,
	} = useQuery("registerFields", GetRegisterFields, {
		enabled: true,
		refetchOnWindowFocus: false,
	});

	const { camps, cities, workers_committees } = registerFields || {};

	const isLoading = isLoadingRegisterFields;

	return (
		<RegistrationFormContext.Provider value={{ 
			isLoading, 
			camps, 
			cities, 
			workers_committees, 
			registerData, 
			setRegisterData, 
			timeLeft, 
			formatTime,
			startTimer
		}}>
			{children}
			<Modal
				isOpen={showTimeoutModal}
				title="מצטערים אבל תם הזמן שהמקום נשמר עבורכם"
				content="בבקשה התחילו מחדש"
				buttonText="המשך"
				onButtonClick={handleTimeoutContinue}
			/>
		</RegistrationFormContext.Provider>
	);
};

export const useRegistrationForm = () => useContext(RegistrationFormContext);
